// /template-lab — DEV-ONLY AI template authoring harness (Jun 11).
//
// Automates the manual template-ingestion loop end-to-end:
//
//   1. Inputs: a MOCK PRODUCT image (file or paste) + a REFERENCE image
//      (paste-first — references are usually screenshots).
//   2. Generate: backend /api/dev/ai-template-lab — RENDER FIRST, THEN
//      WRITE (user-locked Jun 11):
//        a. gpt-image renders with BOTH images as direct input (product
//           + reference) and a fixed recreate instruction — MEDIUM
//           quality at the chosen aspect.
//        b. gpt-4.1 vision then WRITES the template prompt FROM THE
//           GENERATED image (it describes the image that actually
//           exists), with the sanitization contract baked into its
//           system prompt ("the product" only, no packaging
//           descriptions, genericized ingredient props, "Strictly
//           avoid:" closing list).
//   3. Review: preview + the written prompt (EDITABLE — tweak and
//      Re-render; an override skips the vision pass).
//   4. Save: writes the sample WebP + frontend metadata + backend
//      prompt overlay via the Vite dev middleware
//      (/api/dev-ai-template-save). The template is immediately live in
//      /templates + the wizard — backend reads ai_templates_custom.json
//      fresh per render, so no restart needed.
//
// Dev-only: gated on import.meta.env.DEV (the save middleware only
// exists on the dev server anyway). Costs ~$0.01 (vision) + ~$0.07
// (medium render) per generate — no credits charged.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE, getAuthHeaders } from '@/lib/api';

const IS_DEV = import.meta.env.DEV;

type LabAspect = '1:1' | '9:16' | '4:5';

// Locked output dims per aspect (user-specced Jun 11). Backend renders
// at the closest gpt-image native size, center-crops to the aspect,
// then resizes to these exact pixels.
const ASPECTS: { id: LabAspect; label: string }[] = [
  { id: '1:1', label: '1:1 · 1080×1080' },
  { id: '9:16', label: '9:16 · 1080×1920' },
  { id: '4:5', label: '4:5 · 1440×1800' },
];

const CATEGORIES = ['studio', 'model', 'ingredient', 'contextual', 'lifestyle'] as const;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const resp = await fetch(dataUrl);
  return resp.blob();
}

/** Center-crop a data-URL image to a 1024×1024 WebP (q0.9) and return
 *  the base64 payload (no data: prefix) — the library's sample-tile
 *  spec. */
async function toSampleWebpBase64(dataUrl: string): Promise<string> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;
  });
  const SIZE = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  const cover = Math.max(SIZE / img.naturalWidth, SIZE / img.naturalHeight);
  const dw = img.naturalWidth * cover;
  const dh = img.naturalHeight * cover;
  ctx.drawImage(img, (SIZE - dw) / 2, (SIZE - dh) / 2, dw, dh);
  const blob = await new Promise<Blob | null>((r) =>
    canvas.toBlob(r, 'image/webp', 0.9),
  );
  if (!blob) throw new Error('WebP encode failed');
  const buf = await blob.arrayBuffer();
  let bin = '';
  const arr = new Uint8Array(buf);
  for (let i = 0; i < arr.length; i += 0x8000) {
    bin += String.fromCharCode(...arr.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

export default function TemplateLabPage() {
  // ---------- Inputs ----------
  const [productFile, setProductFile] = useState<File | null>(null);
  const [productPreview, setProductPreview] = useState<string | null>(null);
  const [refFile, setRefFile] = useState<File | null>(null);
  const [refPreview, setRefPreview] = useState<string | null>(null);
  const [aspect, setAspect] = useState<LabAspect>('1:1');
  // Which zone receives a ⌘V paste — reference by default (that's the
  // paste-required input; references are screenshots).
  const [pasteTarget, setPasteTarget] = useState<'reference' | 'product'>('reference');

  // ---------- Generation ----------
  const [editInstruction, setEditInstruction] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState<string | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null); // data URL
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ---------- Save ----------
  const [saveName, setSaveName] = useState('');
  const [saveRoute, setSaveRoute] = useState<'product-shot' | 'product-swap'>('product-shot');
  const [saveCategory, setSaveCategory] = useState<typeof CATEGORIES[number]>('studio');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const saveId = useMemo(() => slugify(saveName), [saveName]);

  const setProduct = useCallback(async (file: File) => {
    setProductFile(file);
    setProductPreview(await fileToDataUrl(file));
  }, []);

  const setReference = useCallback(async (file: File) => {
    setRefFile(file);
    setRefPreview(await fileToDataUrl(file));
  }, []);

  // Page-level paste — routes a clipboard image to the active paste
  // target. Skipped when typing in an input/textarea so text paste
  // isn't hijacked.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (!item) return;
      const file = item.getAsFile();
      if (!file) return;
      e.preventDefault();
      if (pasteTarget === 'product') void setProduct(file);
      else void setReference(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [pasteTarget, setProduct, setReference]);

  // ---------- Generate / Re-render ----------
  const runGenerate = useCallback(async (promptOverride?: string) => {
    if (!productFile || generating) return;
    if (!promptOverride && !refFile) return;
    setGenerating(true);
    setError(null);
    setSavedId(null);
    setGenStatus(promptOverride ? 'Rendering with your edited prompt…' : 'Recreating the reference with your product…');
    try {
      const fd = new FormData();
      fd.append('product', productFile);
      if (refFile) fd.append('reference', refFile);
      fd.append('aspect_ratio', aspect);
      if (promptOverride) fd.append('prompt_override', promptOverride);
      if (!promptOverride) {
        // Render (slow, ~30-60s at medium) comes FIRST; the vision
        // prompt-writing pass runs after. Flip the status once the
        // render is plausibly done.
        setTimeout(() => setGenStatus((s) => (s ? 'Writing the template prompt from the result…' : s)), 40000);
      }
      const res = await fetch(`${API_BASE}/api/dev/ai-template-lab`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResultImage(`data:image/png;base64,${data.image_b64}`);
      setPrompt(data.prompt);
      // Fresh generates come with an AI-suggested name (the vision
      // writer names the scene) — prefill so Save is one click. Edits
      // welcome; re-renders (prompt_override) don't clobber it.
      if (!promptOverride && data.name_suggestion) {
        setSaveName(data.name_suggestion);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed.');
    } finally {
      setGenerating(false);
      setGenStatus(null);
    }
  }, [productFile, refFile, aspect, generating]);

  // ---------- Edit the generated image ----------
  // Surgical instruction applied to the CURRENT result ("remove the
  // second can"). Backend edits the image, then re-writes the template
  // prompt from the edited result — prompt + sample stay consistent.
  const runEdit = useCallback(async () => {
    const instruction = editInstruction.trim();
    if (!productFile || !resultImage || !instruction || generating) return;
    setGenerating(true);
    setError(null);
    setSavedId(null);
    setGenStatus('Applying your edit…');
    setTimeout(() => setGenStatus((s) => (s ? 'Writing the updated prompt…' : s)), 40000);
    try {
      const fd = new FormData();
      fd.append('product', productFile);
      fd.append('aspect_ratio', aspect);
      fd.append('edit_instruction', instruction);
      fd.append('base_image', await dataUrlToBlob(resultImage), 'base.png');
      const res = await fetch(`${API_BASE}/api/dev/ai-template-lab`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResultImage(`data:image/png;base64,${data.image_b64}`);
      setPrompt(data.prompt);
      setEditInstruction('');
      // Keep a name the user already settled on; fill only when empty.
      if (!saveName && data.name_suggestion) setSaveName(data.name_suggestion);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Edit failed.');
    } finally {
      setGenerating(false);
      setGenStatus(null);
    }
  }, [editInstruction, productFile, resultImage, aspect, generating, saveName]);

  // ---------- Restore last run ----------
  // Every successful generation persists server-side (image + prompt +
  // meta) — if a dev-server reload kills the page mid-flight or right
  // after, this pulls the most recent run back instead of re-spending
  // the render cost.
  const [restoring, setRestoring] = useState(false);
  const handleRestoreLast = useCallback(async () => {
    if (restoring || generating) return;
    setRestoring(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/dev/ai-template-lab/last`, {
        headers: await getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `No persisted run found (HTTP ${res.status})`);
      }
      const data = await res.json();
      setResultImage(`data:image/png;base64,${data.image_b64}`);
      setPrompt(data.prompt ?? '');
      if (data.aspect_ratio) setAspect(data.aspect_ratio as LabAspect);
      if (!saveName && data.name_suggestion) setSaveName(data.name_suggestion);
      setSavedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setRestoring(false);
    }
  }, [restoring, generating, saveName]);

  // ---------- Save / Cancel ----------
  const handleSave = useCallback(async () => {
    if (!resultImage || !prompt.trim() || !saveId || saving) return;
    setSaving(true);
    setError(null);
    try {
      const sampleWebpBase64 = await toSampleWebpBase64(resultImage);
      const sourcePngBase64 = resultImage.split(',')[1];
      const res = await fetch('/api/dev-ai-template-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: saveId,
          name: saveName.trim(),
          category: saveCategory,
          route: saveRoute,
          prompt: prompt.trim(),
          sampleWebpBase64,
          sourcePngBase64,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setSavedId(saveId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [resultImage, prompt, saveId, saveName, saveCategory, saveRoute, saving]);

  const handleCancel = useCallback(() => {
    setResultImage(null);
    setPrompt('');
    setSaveName('');
    setSavedId(null);
    setError(null);
  }, []);

  if (!IS_DEV) {
    return (
      <div className="min-h-screen flex items-center justify-center text-dark-muted text-sm">
        Template Lab is a dev-only tool — run it on the local dev server.
      </div>
    );
  }

  const canGenerate = !!productFile && !!refFile && !generating;

  return (
    <div className="min-h-screen bg-surface px-6 py-8">
      <div className="max-w-[1400px] mx-auto">

        {/* ---- Header ---- */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-dark text-2xl flex items-center gap-2">
              <i className="ri-flask-line text-primary" />
              Template Lab
              <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-wide">
                dev
              </span>
            </h1>
            <p className="text-dark-muted text-sm mt-1 max-w-2xl">
              Paste a reference image, drop in a mock product — the Lab
              recreates the scene with your product (OpenAI, medium
              quality), then has OpenAI write the template prompt FROM
              the generated image. Save publishes it straight into the
              template library.
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <button
              type="button"
              onClick={() => void handleRestoreLast()}
              disabled={restoring || generating}
              title="Every generation is persisted server-side — recover the most recent one after an accidental page reload"
              className="inline-flex items-center gap-1.5 text-sm text-dark-muted hover:text-primary transition-colors cursor-pointer disabled:opacity-50"
            >
              <i className={restoring ? 'ri-loader-4-line animate-spin' : 'ri-history-line'} />
              Restore last run
            </button>
            <a href="/templates" className="text-sm text-primary hover:underline">
              Open /templates →
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* ============ LEFT: inputs ============ */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Product zone */}
              <InputZone
                title="1 · Mock product"
                hint="Click to upload — or set as paste target and ⌘V"
                preview={productPreview}
                isPasteTarget={pasteTarget === 'product'}
                onActivate={() => setPasteTarget('product')}
                onFile={(f) => void setProduct(f)}
              />
              {/* Reference zone */}
              <InputZone
                title="2 · Reference image"
                hint="Click this box, then ⌘V to paste your screenshot"
                preview={refPreview}
                isPasteTarget={pasteTarget === 'reference'}
                onActivate={() => setPasteTarget('reference')}
                onFile={(f) => void setReference(f)}
              />
            </div>

            {/* Aspect + quality */}
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">Aspect</span>
                <div className="flex p-0.5 rounded-full bg-surface border border-surface-dark/30">
                  {ASPECTS.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setAspect(a.id)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${aspect === a.id ? 'bg-white text-primary shadow' : 'text-dark-muted'}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
              <span className="text-xs text-dark-muted">
                Quality: <strong className="text-dark">Medium</strong> (locked — library-sample grade, ~$0.07/render)
              </span>
            </div>

            <button
              type="button"
              onClick={() => runGenerate()}
              disabled={!canGenerate}
              className={`
                w-full inline-flex items-center justify-center gap-2
                px-6 py-3.5 rounded-full text-sm font-bold transition-all cursor-pointer
                ${canGenerate
                  ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                  : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
              `}
            >
              {generating ? (
                <><i className="ri-loader-4-line animate-spin" /> {genStatus ?? 'Generating…'}</>
              ) : (
                <><i className="ri-sparkling-2-line" /> Generate template</>
              )}
            </button>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Prompt (after generation) — editable + re-render */}
            {prompt && (
              <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-dark flex items-center gap-2">
                    <i className="ri-file-text-line text-base text-primary" />
                    Template prompt (editable)
                  </p>
                  <span className="text-[11px] text-dark-muted">{prompt.length} chars</span>
                </div>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={14}
                  className="w-full px-3 py-2.5 rounded-xl bg-surface/60 border border-dark-muted/30 text-xs leading-relaxed text-dark font-mono focus:outline-none focus:border-primary"
                />
                <button
                  type="button"
                  onClick={() => runGenerate(prompt)}
                  disabled={generating || !productFile || !prompt.trim()}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-primary/40 bg-white text-primary text-xs font-bold hover:bg-primary-light transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-refresh-line" />
                  Re-render with edited prompt (~$0.07)
                </button>
              </div>
            )}
          </div>

          {/* ============ RIGHT: result + save ============ */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border-2 border-surface-dark/30 overflow-hidden">
              <div className="bg-dark/95 flex items-center justify-center p-4 min-h-[320px]">
                {resultImage ? (
                  <img
                    src={resultImage}
                    alt="Generated template sample"
                    className="max-w-full max-h-[560px] rounded-lg"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-white/40 py-16">
                    {generating ? (
                      <>
                        <i className="ri-loader-4-line text-3xl animate-spin" />
                        <span className="text-xs font-semibold">{genStatus}</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-image-2-line text-4xl" />
                        <span className="text-xs font-semibold">
                          Generated sample previews here
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Edit box — surgical changes to the current result. */}
            {resultImage && (
              <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-dark flex items-center gap-2 mb-2">
                  <i className="ri-eraser-line text-base text-primary" />
                  Edit the image
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editInstruction}
                    onChange={(e) => setEditInstruction(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void runEdit(); }}
                    placeholder='e.g. "remove the second can" or "soften the shadows"'
                    maxLength={200}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-dark-muted/40 text-sm text-dark placeholder:text-dark-muted/60 focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => void runEdit()}
                    disabled={!editInstruction.trim() || generating || saving}
                    className={`
                      flex-shrink-0 inline-flex items-center gap-1.5
                      px-4 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer
                      ${editInstruction.trim() && !generating && !saving
                        ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                        : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
                    `}
                  >
                    <i className="ri-magic-line" />
                    Apply
                  </button>
                </div>
                <p className="text-[11px] text-dark-muted mt-2">
                  Changes only what you ask — everything else stays put. The
                  template prompt is re-written from the edited result
                  (~$0.07 + ~$0.01).
                </p>
              </div>
            )}

            {/* Save panel */}
            {resultImage && (
              <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-4">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-dark flex items-center gap-2 mb-3">
                  <i className="ri-save-line text-base text-primary" />
                  Save as template
                </p>
                {savedId ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    <p className="font-bold flex items-center gap-1.5">
                      <i className="ri-check-line" />
                      Saved as <code className="px-1.5 py-0.5 rounded bg-emerald-100">{savedId}</code>
                    </p>
                    <p className="text-xs mt-1.5">
                      Live now in <a href="/templates" className="underline font-semibold">/templates</a> and
                      the wizard — the backend reads the prompt overlay fresh per
                      render, no restart needed. Commit
                      <code className="mx-1 px-1 py-0.5 rounded bg-emerald-100 text-[11px]">ai-templates-custom.json</code>
                      (both sides) + the sample WebP to ship it to prod.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        placeholder="Template name — e.g. Marble Dawn"
                        maxLength={24}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-dark-muted/40 text-sm text-dark placeholder:text-dark-muted/60 focus:outline-none focus:border-primary"
                      />
                      <span className="text-[11px] text-dark-muted font-mono flex-shrink-0">
                        id: {saveId || '—'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                      <label className="flex items-center gap-2 text-xs font-bold text-dark-muted uppercase tracking-wide">
                        Route
                        <select
                          value={saveRoute}
                          onChange={(e) => setSaveRoute(e.target.value as 'product-shot' | 'product-swap')}
                          className="px-3 py-1.5 rounded-lg border border-dark-muted/30 bg-white text-sm text-dark normal-case font-medium"
                        >
                          <option value="product-shot">Product Shot</option>
                          <option value="product-swap">In Hand</option>
                        </select>
                      </label>
                      <label className="flex items-center gap-2 text-xs font-bold text-dark-muted uppercase tracking-wide">
                        Category
                        <select
                          value={saveCategory}
                          onChange={(e) => setSaveCategory(e.target.value as typeof CATEGORIES[number])}
                          className="px-3 py-1.5 rounded-lg border border-dark-muted/30 bg-white text-sm text-dark normal-case font-medium"
                        >
                          {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={!saveId || saving}
                        className={`
                          flex-1 inline-flex items-center justify-center gap-2
                          px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer
                          ${saveId && !saving
                            ? 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'
                            : 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'}
                        `}
                      >
                        {saving ? (
                          <><i className="ri-loader-4-line animate-spin" /> Saving…</>
                        ) : (
                          <><i className="ri-save-line" /> Save to library</>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-6 py-3 rounded-full border border-surface-dark/40 bg-white text-dark text-sm font-bold hover:bg-surface transition-colors cursor-pointer disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// InputZone — image input with file picker + paste-target activation.
// Clicking the zone makes it the ⌘V target (ring highlight); the inner
// button opens the file picker as a fallback for both zones.
// =============================================================================

function InputZone({ title, hint, preview, isPasteTarget, onActivate, onFile }: {
  title: string;
  hint: string;
  preview: string | null;
  isPasteTarget: boolean;
  onActivate: () => void;
  onFile: (f: File) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onFocus={onActivate}
      className={`
        bg-white rounded-2xl border-2 p-3 cursor-pointer transition-all
        ${isPasteTarget ? 'border-primary ring-2 ring-primary/25' : 'border-surface-dark/30 hover:border-primary/40'}
      `}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-dark">{title}</p>
        {isPasteTarget && (
          <span className="text-[10px] font-bold text-primary uppercase tracking-wide">
            ⌘V target
          </span>
        )}
      </div>
      <div className="relative aspect-square rounded-xl bg-surface overflow-hidden flex items-center justify-center">
        {preview ? (
          <img src={preview} alt="" className="absolute inset-0 w-full h-full object-contain" />
        ) : (
          <p className="text-[11px] text-dark-muted text-center px-4">{hint}</p>
        )}
      </div>
      <label
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-primary hover:underline cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <i className="ri-upload-2-line" />
        {preview ? 'Replace from file' : 'Upload from file'}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = '';
          }}
        />
      </label>
    </div>
  );
}
